import { useEffect } from 'react'
import { speakAnnouncement } from '@services/tts'

type Props = {
  open: boolean
  onClose: () => void
  data: any | null
  lang: string
}

export default function POIDetails({ open, onClose, data, lang }: Props) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const extract = data?.summary?.extract
  let thumb = data?.summary?.thumbnail?.source
  
  if (thumb && thumb.includes('/api/img?url=')) {
    try {
      const qsStart = thumb.indexOf('?url=') + 5
      const urlStr = thumb.substring(qsStart)
      thumb = decodeURIComponent(urlStr)
    } catch (e) {
      // fallback to original if decode fails
    }
  }

  const attr = data?.summary?.attribution
  const contentLang: string | undefined = data?.summary?.lang
  const fallback = contentLang && contentLang !== lang

  const handleSpeak = () => {
    // Use the Wikipedia extract (which is already in the selected language when
    // fetched online). Fall back to interesting_fact or just the name.
    const spokenText = [
      data?.name,
      extract || data?.interesting_fact || ''
    ].filter(Boolean).join('. ')
    speakAnnouncement(spokenText, lang)
  }

  return (
    <div className="fixed inset-0 z-[3000] flex items-center justify-center p-4 bg-on-background/40 backdrop-blur-sm transition-opacity" onClick={onClose}>
      <div className="bg-surface-container-lowest w-full max-w-lg md:max-w-xl rounded-[2rem] shadow-2xl overflow-hidden flex flex-col transform transition-all" onClick={e => e.stopPropagation()}>
        {thumb ? (
          <div className="h-56 md:h-64 w-full relative bg-surface-container">
            <img src={thumb} alt={data?.name} className="w-full h-full object-cover" />
            <button onClick={onClose} className="absolute top-4 right-4 bg-black/40 text-white p-2 md:p-2.5 rounded-full hover:bg-black/70 transition-colors flex items-center justify-center backdrop-blur-md">
              <span className="material-symbols-outlined text-[20px]">close</span>
            </button>
            <div className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-black/60 to-transparent"></div>
            <h2 className="absolute bottom-4 left-6 md:left-8 font-headline text-2xl md:text-3xl font-extrabold text-white tracking-tight drop-shadow-md">{data?.name}</h2>
          </div>
        ) : (
          <div className="flex justify-between items-start p-6 pb-0">
            <h2 className="font-headline text-2xl md:text-3xl font-extrabold text-on-surface tracking-tight">{data?.name}</h2>
            <div className="flex items-center gap-1">
              <button
                onClick={handleSpeak}
                title="Read aloud"
                className="text-on-surface-variant hover:text-primary p-2 rounded-full hover:bg-surface-container transition-colors flex items-center justify-center"
              >
                <span className="material-symbols-outlined text-[20px]">volume_up</span>
              </button>
              <button onClick={onClose} className="text-on-surface-variant hover:text-on-surface p-2 rounded-full hover:bg-surface-container transition-colors flex items-center justify-center">
                <span className="material-symbols-outlined text-[20px]">close</span>
              </button>
            </div>
          </div>
        )}

        <div className="p-6 md:p-8 flex flex-col gap-4 max-h-[50vh] overflow-y-auto no-scrollbar">
          {/* When there's a thumbnail the speak button sits here instead */}
          {thumb && (
            <button
              onClick={handleSpeak}
              title="Read aloud"
              className="self-start flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-primary hover:text-tertiary transition-colors"
            >
              <span className="material-symbols-outlined text-[18px]">volume_up</span>
              Read aloud
            </button>
          )}

          <p className="text-on-surface-variant leading-relaxed text-sm md:text-base font-medium">{extract || 'Content unavailable offline.'}</p>

          <div className="mt-4 flex flex-col gap-2 pt-6 border-t border-outline-variant/20">
            {contentLang && <span className="text-[10px] text-outline font-bold uppercase tracking-widest">Language: {contentLang}{fallback ? ' (fallback)' : ''}</span>}
            <span className="text-[10px] text-outline uppercase tracking-widest">
              {attr ? (
                <span className="flex flex-wrap gap-1">
                  Source: <a href={attr.url} target="_blank" rel="noreferrer" className="text-primary hover:text-tertiary transition-colors">{attr.source}</a><span className="mx-1">·</span>License: {attr.license}
                </span>
              ) : (
                <span>Attribution unavailable</span>
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
